import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Upload, X, Save, ArrowUp, ArrowDown, Home, Layers } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "slaughterhouse", label: "Thertorja" },
  { value: "salami", label: "Sallameria" },
  { value: "stables", label: "Stalla" },
  { value: "transport", label: "Transporti" },
  { value: "team", label: "Ekipi" },
];

const GalleryForm = ({ item, onClose, onSaved, authHeaders }) => {
  const [data, setData] = useState(item || { title_sq: "", title_en: "", image_url: "", category: "slaughterhouse", show_on_home: false, order: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!item?.id;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error("Foto më e madhe se 100MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      set("image_url", res.url);
      toast.success("Foto u ngarkua");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim në ngarkim");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.image_url) { toast.error("Ngarko një foto"); return; }
    setSaving(true);
    try {
      const payload = { title_sq: data.title_sq, title_en: data.title_en, image_url: data.image_url, category: data.category, show_on_home: data.show_on_home, order: data.order };
      if (isEdit) {
        await axios.put(`${API}/admin/gallery/${item.id}`, payload, { headers: authHeaders() });
        toast.success("U përditësua");
      } else {
        await axios.post(`${API}/admin/gallery`, payload, { headers: authHeaders() });
        toast.success("U shtua në galeri");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gabim");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="gallery-form-modal">
      <form onSubmit={onSubmit} className="bg-white w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{isEdit ? "Ndrysho Foton" : "Shto Foto në Galeri"}</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Foto *</label>
            {data.image_url ? (
              <div className="relative aspect-video bg-zinc-100 mb-2">
                <img src={data.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                <button type="button" onClick={() => set("image_url", "")} className="absolute top-2 right-2 bg-red-600 text-white p-1.5"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <label className="block aspect-video border-2 border-dashed border-zinc-300 cursor-pointer hover:border-[#0A0A0A] flex items-center justify-center">
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                  <span className="text-sm text-zinc-500">{uploading ? "Duke ngarkuar..." : "Klik për ngarkim"}</span>
                </div>
                <input data-testid="gallery-upload" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} disabled={uploading} className="hidden" />
              </label>
            )}
            <p className="text-xs text-zinc-500 mt-1">JPG, PNG, WEBP. Max 100MB.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Titull (SQ)</label>
              <input data-testid="gallery-title-sq" value={data.title_sq} onChange={(e) => set("title_sq", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Title (EN)</label>
              <input value={data.title_en} onChange={(e) => set("title_en", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Kategoria</label>
              <select data-testid="gallery-category" value={data.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Renditja</label>
              <input type="number" value={data.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none" />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="gallery-on-home" checked={data.show_on_home} onChange={(e) => set("show_on_home", e.target.checked)} className="w-4 h-4" />
            <span className="text-sm font-medium">Shfaq në Homepage</span>
          </label>
        </div>
        <div className="flex gap-3 p-6 border-t border-zinc-200">
          <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border border-zinc-300 hover:bg-zinc-50">Anulo</button>
          <button data-testid="gallery-save-button" type="submit" disabled={saving} className="flex-1 px-6 py-3 bg-[#0A0A0A] text-white font-medium hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </form>
    </div>
  );
};

const BulkUploadForm = ({ onClose, onSaved, authHeaders }) => {
  const [category, setCategory] = useState("slaughterhouse");
  const [showOnHome, setShowOnHome] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [running, setRunning] = useState(false);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = picked.filter((f) => f.size <= 100 * 1024 * 1024);
    if (valid.length < picked.length) {
      toast.warning(`${picked.length - valid.length} foto u përjashtuan (mbi 100MB)`);
    }
    setFiles(valid);
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) { toast.error("Zgjidh të paktën 1 foto"); return; }
    setRunning(true);
    setProgress({ done: 0, total: files.length, current: "" });
    let success = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress({ done: i, total: files.length, current: file.name });
      try {
        // 1) upload image
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        });
        // 2) create gallery item
        const title = file.name.replace(/\.[^.]+$/, "");
        await axios.post(
          `${API}/admin/gallery`,
          { title_sq: title, title_en: title, image_url: up.url, category, show_on_home: showOnHome, order: i },
          { headers: authHeaders() }
        );
        success++;
      } catch (err) {
        failed++;
        if (process.env.NODE_ENV !== 'production') console.error("Bulk upload failed for", file.name, err);
      }
    }
    setRunning(false);
    if (success > 0) toast.success(`${success} foto u shtuan në galeri`);
    if (failed > 0) toast.error(`${failed} foto dështuan`);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" data-testid="gallery-bulk-modal">
      <form onSubmit={onSubmit} className="bg-white w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <Layers className="w-5 h-5" /> Ngarko Disa Foto
          </h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Kategoria *</label>
              <select data-testid="bulk-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-zinc-300 px-3 py-2 focus:border-[#0A0A0A] focus:outline-none">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <p className="text-xs text-zinc-500 mt-1">Të gjitha fotot do shkojnë në këtë kategori</p>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={showOnHome} onChange={(e) => setShowOnHome(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm font-medium">Shfaq në Homepage</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Fotot ({files.length})</label>
            <label className="block border-2 border-dashed border-zinc-300 p-8 cursor-pointer hover:border-[#0A0A0A] text-center">
              <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
              <span className="text-sm text-zinc-600">Klik për të zgjedhur disa foto</span>
              <p className="text-xs text-zinc-500 mt-1">JPG, PNG, WEBP. Max 100MB për foto.</p>
              <input
                data-testid="bulk-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={onPick}
                disabled={running}
                className="hidden"
              />
            </label>

            {files.length > 0 && (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {files.map((f, i) => (
                  <div key={i} className="relative group aspect-square bg-zinc-100 border border-zinc-200">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeFile(i)} disabled={running} className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100">
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-1 py-0.5 truncate">{f.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {running && (
            <div className="bg-zinc-50 border border-zinc-200 p-4">
              <p className="text-sm font-medium">Duke ngarkuar {progress.done + 1}/{progress.total}: {progress.current}</p>
              <div className="mt-2 h-2 bg-zinc-200 overflow-hidden">
                <div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t border-zinc-200">
          <button type="button" onClick={onClose} disabled={running} className="flex-1 px-6 py-3 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50">Anulo</button>
          <button data-testid="bulk-submit" type="submit" disabled={running || files.length === 0} className="flex-1 px-6 py-3 bg-[#0A0A0A] text-white font-medium hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {running ? "Duke ngarkuar..." : `Ngarko ${files.length || ""}`}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminGallery = () => {
  const { authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gallery`, { headers: authHeaders() });
      setItems(data);
    } catch {
      toast.error("Gabim duke ngarkuar");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (item) => {
    if (!window.confirm("Fshi këtë foto?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${item.id}`, { headers: authHeaders() });
      toast.success("U fshi");
      load();
    } catch {
      toast.error("Gabim");
    }
  };

  const moveOrder = async (item, delta) => {
    try {
      await axios.put(`${API}/admin/gallery/${item.id}`, { order: (item.order || 0) + delta }, { headers: authHeaders() });
      load();
    } catch {
      toast.error("Gabim");
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-gallery-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Galeria</h1>
            <p className="text-zinc-600 mt-1">Menaxho fotot e galerisë</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="bulk-upload-button" onClick={() => setBulkOpen(true)} className="bg-white border border-[#0A0A0A] text-[#0A0A0A] px-5 py-3 font-medium hover:bg-zinc-100 flex items-center gap-2">
              <Layers className="w-4 h-4" /> Ngarko Disa Foto
            </button>
            <button data-testid="add-gallery-button" onClick={() => setEditing({})} className="bg-[#0A0A0A] text-white px-5 py-3 font-medium hover:bg-zinc-800 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Shto Foto
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500">Duke ngarkuar...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
            <p>Galeria është bosh</p>
            <p className="text-sm mt-2">Klik "Shto Foto" për të filluar</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <div key={item.id} className="bg-white border border-zinc-200 overflow-hidden" data-testid="gallery-item-row">
                <div className="aspect-video bg-zinc-100 relative">
                  <img src={item.image_url} alt={item.title_sq} loading="lazy" className="w-full h-full object-cover" />
                  {item.show_on_home && (
                    <span className="absolute top-2 left-2 bg-[#0A0A0A] text-white text-xs px-2 py-1 flex items-center gap-1">
                      <Home className="w-3 h-3" /> Home
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-medium text-[#0A0A0A] truncate">{item.title_sq || "(pa titull)"}</p>
                  <p className="text-xs text-zinc-500">{CATEGORIES.find((c) => c.value === item.category)?.label || item.category} • #{item.order}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => moveOrder(item, -1)} className="p-1.5 border border-zinc-300 hover:bg-zinc-50"><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveOrder(item, 1)} className="p-1.5 border border-zinc-300 hover:bg-zinc-50"><ArrowDown className="w-3 h-3" /></button>
                    <button data-testid="edit-gallery-button" onClick={() => setEditing(item)} className="flex-1 px-3 py-1.5 text-sm border border-zinc-300 hover:bg-zinc-50">Ndrysho</button>
                    <button data-testid="delete-gallery-button" onClick={() => onDelete(item)} className="p-1.5 text-red-600 hover:bg-red-50 border border-red-200"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing !== null && (
          <GalleryForm
            item={editing.id ? editing : null}
            authHeaders={authHeaders}
            onClose={() => setEditing(null)}
            onSaved={load}
          />
        )}

        {bulkOpen && (
          <BulkUploadForm
            authHeaders={authHeaders}
            onClose={() => setBulkOpen(false)}
            onSaved={load}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
